/** BUTONUL DE EXPORT al unui modul: un semn, cu formatele sub el.
 *
 *  Era un buton «Export PDF». Cu al doilea format, două butoane alăturate ar
 *  fi umplut bara cu două verbe pentru același gest; un singur buton de export
 *  deschide lista formatelor — PDF, DWG —, iar fiecare rând își spune ce dă.
 *  Aceeași mecanică de meniu ca `MeniuCont`: se închide la clic în afară și
 *  la Escape.
 *
 *  Stă și pe cartonașul din colecție (`compact`): acolo nu e un buton cu
 *  chenar, e un semn care apare la trecerea mouse-ului, ca «redenumește»; iar
 *  clicul pe el nu deschide modulul — cartonașul ascultă de propriul clic, și
 *  i se oprește aici.
 */
import { useEffect, useRef, useState } from "react";
import { Ic } from "../../../components/ui";
import { useT } from "../../../i18n";

export type FormatExport = "pdf" | "dwg";

/** Un rând al meniului: formatul dat înapoi la alegere, textul și tooltip-ul. */
export type RandExport<F extends string> = { format: F; text: string; titlu?: string };

export default function MeniuExport<F extends string = FormatExport>({
  laExport, lucrez = false, disabled = false, compact = false, randuri, titlu,
}: {
  laExport: (format: F) => void;
  /** Un export e în lucru: butonul arată că așteaptă și nu mai primește altul. */
  lucrez?: boolean;
  disabled?: boolean;
  compact?: boolean;
  /** Formatele oferite; implicit cele ale modulului (PDF, DWG). Planșa PUD
   *  (`pages/Pud`) dă ale ei (PDF, DXF), cu același buton. */
  randuri?: RandExport<F>[];
  /** Tooltip-ul butonului; implicit «Export». */
  titlu?: string;
}) {
  const t = useT();
  const formate: RandExport<F>[] = randuri ?? ([
    { format: "pdf", text: t("export.pdf"), titlu: t("export.pdf_titlu") },
    { format: "dwg", text: t("export.dwg"), titlu: t("export.dwg_titlu") },
  ] as RandExport<string>[] as RandExport<F>[]);
  const eticheta = titlu ?? t("export.buton");
  const [deschis, setDeschis] = useState(false);
  const gazda = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!deschis) return;
    const inafara = (e: MouseEvent) => {
      if (!gazda.current?.contains(e.target as Node)) setDeschis(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setDeschis(false); };
    document.addEventListener("mousedown", inafara);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", inafara);
      document.removeEventListener("keydown", esc);
    };
  }, [deschis]);

  const alege = (format: F) => {
    setDeschis(false);
    laExport(format);
  };

  return (
    <div className={`ob-export${compact ? " compact" : ""}`} ref={gazda}
         onClick={(e) => e.stopPropagation()}
         onKeyDown={(e) => e.stopPropagation()}>
      <button type="button"
              className={`${compact ? "ob-export-semn" : "btn"} ob-export-buton${deschis ? " on" : ""}`}
              onClick={() => setDeschis((d) => !d)}
              disabled={disabled || lucrez}
              aria-haspopup="menu" aria-expanded={deschis}
              aria-label={eticheta} title={eticheta}>
        {lucrez ? "…" : <Ic n="download" s={compact ? 13 : 15} />}
      </button>
      {deschis && (
        <div className="card ob-export-meniu" role="menu">
          {formate.map((r) => (
            <button key={r.format} type="button" className="ob-export-item" role="menuitem"
                    title={r.titlu} onClick={() => alege(r.format)}>
              {r.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
